// src/AttachmentForm.js
//
// NEW CONCEPT: uploading an actual FILE is different from every other
// form so far. We can't just JSON.stringify() a file - browsers can't
// put raw file bytes inside a JSON string. Instead we use FormData,
// a browser-native object designed exactly for this: it bundles text
// fields AND real files together into "multipart/form-data", the
// format file uploads use.
//
// Because of this, we do NOT set 'Content-Type': 'application/json'
// here - we let the browser set the Content-Type header itself
// (fetch does this automatically for FormData, including the special
// "boundary" marker multipart uploads require).

import { useState, useEffect } from 'react';
import { API_BASE_URL } from './apiConfig';
import { formatApiError } from './formErrors';

function AttachmentForm({ token, attachment, onSaved, onCancel }) {
  const [projectOptions, setProjectOptions] = useState([]);
  const [activityOptions, setActivityOptions] = useState([]);
  const [impactRecordOptions, setImpactRecordOptions] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);

  const [title, setTitle] = useState(attachment?.title || '');
  const [uploadedBy, setUploadedBy] = useState(attachment?.uploaded_by || '');
  const [project, setProject] = useState(attachment?.project || '');
  const [activity, setActivity] = useState(attachment?.activity || '');
  const [impactRecord, setImpactRecord] = useState(attachment?.impact_record || '');
  // file holds the actual File object the user picked, starts as null
  // since editing an existing attachment doesn't require re-uploading -
  // the old file just stays as-is unless a new one is chosen.
  const [file, setFile] = useState(null);

  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const headers = { Authorization: `Token ${token}` };
    fetch(`${API_BASE_URL}/api/projects/`, { headers })
      .then((res) => res.json()).then((data) => { if (Array.isArray(data)) setProjectOptions(data); });
    fetch(`${API_BASE_URL}/api/activities/`, { headers })
      .then((res) => res.json()).then((data) => { if (Array.isArray(data)) setActivityOptions(data); });
    fetch(`${API_BASE_URL}/api/impact-records/`, { headers })
      .then((res) => res.json()).then((data) => { if (Array.isArray(data)) setImpactRecordOptions(data); });
    fetch(`${API_BASE_URL}/api/staff/`, { headers })
      .then((res) => res.json()).then((data) => { if (Array.isArray(data)) setStaffOptions(data); });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic guard: a NEW attachment must include a file (the model's
    // FileField is required) - editing doesn't need a fresh file.
    if (!attachment && !file) {
      setError('Please choose a file to upload.');
      return;
    }

    setSaving(true);
    setError('');

    // Build a FormData object instead of a plain JS object. .append()
    // adds one field at a time - text fields as strings, and the file
    // itself as the actual File object from the <input type="file">.
    const formData = new FormData();
    formData.append('title', title);
    if (uploadedBy) formData.append('uploaded_by', uploadedBy);
    if (project) formData.append('project', project);
    if (activity) formData.append('activity', activity);
    if (impactRecord) formData.append('impact_record', impactRecord);
    if (file) formData.append('file', file);

    const url = attachment
      ? `${API_BASE_URL}/api/attachments/${attachment.id}/`
      : `${API_BASE_URL}/api/attachments/`;
    const method = attachment ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Token ${token}`,
          // Deliberately NOT setting Content-Type here - the browser
          // sets it automatically for FormData, with the correct
          // multipart boundary. Setting it manually would break the
          // upload.
        },
        body: formData,
      });
      if (!response.ok) {
        setError(formatApiError(await response.json()));
        setSaving(false);
        return;
      }
      onSaved();
    } catch (err) {
      setError('Could not connect to the server');
      setSaving(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded shadow mb-6 max-w-lg">
      <h2 className="text-lg font-bold mb-4">{attachment ? 'Edit Attachment' : 'New Attachment'}</h2>
      <form onSubmit={handleSubmit}>
        <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
        <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} required
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400" />

        <label className="block text-sm font-medium text-gray-700 mb-1">
          File {attachment && '(leave empty to keep the existing file)'}
        </label>
        <input
          type="file"
          // File inputs are ALWAYS uncontrolled in React - you cannot
          // set `value` on them (browsers block it for security, so a
          // page can't pre-fill a fake file). Instead we just listen
          // for onChange and grab the picked file out of
          // e.target.files[0] (the [0] because file inputs support
          // multiple files - we only take the first one here).
          onChange={(e) => setFile(e.target.files[0])}
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4"
        />

        <label className="block text-sm font-medium text-gray-700 mb-1">Uploaded By</label>
        <select value={uploadedBy} onChange={(e) => setUploadedBy(e.target.value)}
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400">
          <option value="">Not specified</option>
          {staffOptions.map((s) => (
            <option key={s.id} value={s.id}>{s.full_name}</option>
          ))}
        </select>

        <label className="block text-sm font-medium text-gray-700 mb-1">Link to Project (optional)</label>
        <select value={project} onChange={(e) => setProject(e.target.value)}
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400">
          <option value="">None</option>
          {projectOptions.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>

        <label className="block text-sm font-medium text-gray-700 mb-1">Link to Activity (optional)</label>
        <select value={activity} onChange={(e) => setActivity(e.target.value)}
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400">
          <option value="">None</option>
          {activityOptions.map((a) => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>

        <label className="block text-sm font-medium text-gray-700 mb-1">Link to Impact Record (optional)</label>
        <select value={impactRecord} onChange={(e) => setImpactRecord(e.target.value)}
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400">
          <option value="">None</option>
          {impactRecordOptions.map((ir) => (
            <option key={ir.id} value={ir.id}>{ir.beneficiary_label}</option>
          ))}
        </select>

        {error && <p className="text-red-500 text-sm mb-4 break-words">{error}</p>}

        <div className="flex gap-2">
          <button type="submit" disabled={saving}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Uploading...' : 'Save'}
          </button>
          <button type="button" onClick={onCancel}
            className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default AttachmentForm;
